import { Injectable } from '@angular/core';
import { Observable, map, of, share, tap } from 'rxjs';

import { IUserWithSummaryOrWithoutData } from '@site-mate/dashpivot-shared-library';

import { GlobalApiService } from 'app/global-api/global-api.service';

@Injectable({
  providedIn: 'root',
})
export class SitemateUsersService {
  private userDataCache = new Map<string, Observable<IUserWithSummaryOrWithoutData>>();

  constructor(private globalApiService: GlobalApiService) {}

  getUserDataObservablesInBatches(userIds: string[]): Map<string, Observable<IUserWithSummaryOrWithoutData>> {
    const MAX_BATCH_SIZE = 50;
    const batchedIds: string[][] = [];
    const resultObservables = new Map<string, Observable<IUserWithSummaryOrWithoutData>>();

    for (let i = 0; i < userIds.length; i += MAX_BATCH_SIZE) {
      batchedIds.push(userIds.slice(i, i + MAX_BATCH_SIZE));
    }

    batchedIds.forEach((batch) => {
      this.getUserDataObservables(batch, resultObservables);
    });

    return resultObservables;
  }

  private getUserDataObservables(
    batch: string[],
    resultObservables: Map<string, Observable<IUserWithSummaryOrWithoutData>>,
  ) {
    const usersToGetFromSmApi = batch.filter((userId) => !this.userDataCache.has(userId));

    if (usersToGetFromSmApi.length > 0) {
      const batchObservable = this.globalApiService.getUserInfo(usersToGetFromSmApi).pipe(
        tap((usersData) => {
          usersData.forEach((user) => {
            // Cache the result once we receive the response
            this.userDataCache.set(user._id, of(user));
          });
        }),
        share(),
      );

      // Cache the observable for each user while we wait for the response
      usersToGetFromSmApi.forEach((userId) => {
        this.userDataCache.set(
          userId,
          batchObservable.pipe(map((users) => users.find((user) => user._id === userId))),
        );
      });
    }

    batch.forEach((userId) => {
      resultObservables.set(userId, this.userDataCache.get(userId));
    });
  }
}
